import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { ResponsiveBar } from '@nivo/bar';
import { RecapturedRevenue, RecapturedRevenueAssumptions } from '../../../../../shared/api/controllers/engagementAnalyticsReporting';
import { barChartDefaults, nivoChartTheme } from '../../../../../shared/constants/chartTheme';
import { breakpoint_large, breakpoint_macbook_small, breakpoint_macbook } from '../../../../../shared/constants/breakpoints';
import { chart_planned_solid, color_shades_darkest, color_shades_light, color_text_dashboard_secondary, voicify_spark_dark, voicify_spark_darker, voicify_spark_darkest, voicify_spark_lighter, voicify_spark_lightest } from '../../../../../shared/constants/colors';
import { ChartTooltip } from './ChartTooltip';
import { MetricInfoTooltip } from './MetricInfoTooltip';
import { DEFAULT_RECAPTURED_REVENUE_ASSUMPTIONS } from './recapturedRevenueAssumptions';
import { explainRecapturedRevenueEstimate, getRecapturedRevenueEstimate, RecapturedRevenueEstimateInputs } from './recapturedRevenueEstimate';
import { BaseTile, TileTitle, BigNumber, NumberSubtext, ViewDetailsButton, TileHeader } from './ApplicationDashboard.styles';

interface RecapturedRevenueDashboardProps {
    /** Server estimate: the full calculation in complex mode; in simple mode only its histogram, for when orders came in and how busy the lines were. */
    recapturedRevenue: RecapturedRevenue | null;
    /** Histogram interval the data was loaded with: 0 = hour, 1 = day. */
    interval: number;
    isLoading: boolean;
    /** Dashboard totals the simple estimate is built from. */
    estimateInputs: RecapturedRevenueEstimateInputs;
    /** Share of phone orders the store says it was missing before Voicify, from 0 to 1. */
    missedOrderRate: number;
    /** Called on every move of the simple mode slider. Hides the slider when not set. */
    onMissedOrderRateChange?: (rate: number) => void;
    /** The organization's saved rate the slider starts at; moving away from it offers a reset. */
    defaultMissedOrderRate?: number;
    /** Saves the slider's rate as the organization's default. Shown only to organization admins and platform operators. */
    onSaveMissedOrderRateDefault?: () => Promise<void>;
    /** Complex mode shows the full calculation (busy lines and no answer) with all its assumptions. */
    isComplexMode?: boolean;
    /** Switches modes from the gear button, which is hidden when this is not set. */
    onComplexModeChange?: (isComplexMode: boolean) => void;
    /** Set while a language filter is active: the complex estimate always covers every language. */
    isAllLanguagesOnly?: boolean;
    /** Complex mode assumptions edited with the sliders; null uses the server defaults. */
    editedAssumptions?: RecapturedRevenueAssumptions | null;
    /** Set while the complex estimate is re-run for a new staff count or hold time. */
    isRecalculating?: boolean;
    /** Called on every complex mode slider move (null restores the defaults). */
    onAssumptionsChange?: (assumptions: RecapturedRevenueAssumptions | null) => void;
    /** Selected date range, shown beside the estimate so it's clear the number covers only that range. */
    dateRangeLabel?: string;
    onBack: () => void;
}

const RECAPTURED_KEY = 'Recaptured';

export const RECAPTURED_REVENUE_DEFINITION = {
    name: 'Recaptured Revenue',
    description: 'Sales from the calls the store says it wasn\'t picking up before Voicify, now answered by Voicify. Those calls are assumed to order like the other calls Voicify answers.',
    calculation: 'calls × % missed before Voicify × placed-order rate × average order size'
};

export const COMPLEX_RECAPTURED_REVENUE_DEFINITION = {
    name: 'Recaptured Revenue',
    description: 'Orders the store would likely have lost without the assistant. Busy Lines: the location’s staff would still be on another call past the hold time. No Answer: the line is free but some calls ring out. Transferred calls count only until the transfer.',
    calculation: '(busy-line orders + other orders × unanswered %) × never-order %'
};

type AssumptionKey = keyof RecapturedRevenueAssumptions;

const ASSUMPTION_SLIDERS: Array<{ key: AssumptionKey; label: string; hint: string; min: number; max: number; step: number; format: (value: number) => string; isRate?: boolean }> = [
    { key: 'staffCount', label: 'Staff answering phones per location', hint: 'Calls in progress before a new caller hits a busy line.', min: 0, max: 5, step: 0.1, format: value => value.toFixed(1) },
    { key: 'holdLimitSeconds', label: 'Hold time', hint: 'How long a caller would wait for a free line.', min: 0, max: 300, step: 5, format: value => `${value}s` },
    { key: 'missedWhenFreeRate', label: 'Unanswered calls', hint: 'Calls nobody picks up even when the line is free.', min: 0, max: 100, step: 1, format: value => `${value}%`, isRate: true },
    { key: 'lostCustomerRate', label: 'Callers who never order', hint: 'Missed callers who never call back or order online.', min: 0, max: 100, step: 1, format: value => `${value}%`, isRate: true }
];

const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
/** Fills the slider track up to the thumb (see SliderInput). */
const sliderFill = (value: number, min: number, max: number) => ({ '--fill': `${max > min ? (value - min) / (max - min) * 100 : 0}%` } as React.CSSProperties);
const formatPercent = (rate: number) => `${Math.round((rate || 0) * 100)}%`;

const RecapturedRevenueDashboard: React.FC<RecapturedRevenueDashboardProps> = ({ recapturedRevenue, interval, isLoading, estimateInputs, missedOrderRate, onMissedOrderRateChange, defaultMissedOrderRate, onSaveMissedOrderRateDefault,
    isComplexMode, onComplexModeChange, isAllLanguagesOnly, editedAssumptions, isRecalculating, onAssumptionsChange, dateRangeLabel, onBack }) => {
    const isHourly = interval === 0;
    const assumptions = recapturedRevenue?.assumptions;
    const missedOrderPercent = Math.round(missedOrderRate * 100);
    const defaultMissedOrderPercent = Math.round((defaultMissedOrderRate ?? missedOrderRate) * 100);
    const isAtDefault = missedOrderPercent === defaultMissedOrderPercent;
    const [isSavingDefault, setIsSavingDefault] = useState(false);
    const [saveDefaultError, setSaveDefaultError] = useState('');
    const saveDefault = async () => {
        setIsSavingDefault(true);
        setSaveDefaultError('');
        try {
            await onSaveMissedOrderRateDefault();
        } catch (error) {
            setSaveDefaultError(error instanceof Error ? error.message : 'The default could not be saved. Try again.');
        } finally {
            setIsSavingDefault(false);
        }
    };
    const estimateTitle = isComplexMode ? 'Recaptured Revenue' : 'Est. Recaptured Revenue';
    const estimate = getRecapturedRevenueEstimate(estimateInputs, missedOrderRate);

    const sliderAssumptions = editedAssumptions || assumptions || DEFAULT_RECAPTURED_REVENUE_ASSUMPTIONS;
    const getSliderValue = (key: AssumptionKey, isRate?: boolean) => isRate ? Math.round(sliderAssumptions[key] * 100) : sliderAssumptions[key];
    const setSliderValue = (key: AssumptionKey, value: number, isRate?: boolean) =>
        onAssumptionsChange({ ...sliderAssumptions, [key]: isRate ? value / 100 : Math.round(value * 10) / 10 });

    // Simple mode spreads its estimate over the buckets by when orders were placed, so the bars add up to the headline.
    const histogramOrders = (recapturedRevenue?.histogram || []).reduce((sum, bucket) => sum + (bucket.totalOrders || 0), 0);
    const chartData = useMemo(() => (recapturedRevenue?.histogram || []).map(bucket => {
        const date = moment.parseZone(bucket.date);
        const recaptured = isComplexMode
            ? bucket.recapturedRevenue
            : histogramOrders > 0 ? estimate.recapturedRevenue * (bucket.totalOrders || 0) / histogramOrders : 0;
        // The raw timestamp keeps buckets unique (repeated DST hours, ranges across years); the label is for display.
        return {
            bucket: bucket.date,
            label: isHourly ? date.format('MMM D h A') : date.format('MMM D'),
            [RECAPTURED_KEY]: recaptured,
            total: recaptured,
            totalOrders: bucket.totalOrders,
            maxConcurrentCalls: bucket.maxConcurrentCalls || 0
        };
    }), [recapturedRevenue, isHourly, isComplexMode, histogramOrders, estimate.recapturedRevenue]);

    const hasChartData = chartData.some(datum => datum.total > 0);
    const hasConcurrencyData = chartData.some(datum => datum.maxConcurrentCalls > 0);
    const maxConcurrentCalls = recapturedRevenue?.maxConcurrentCalls || 0;
    const concurrencyTicks = Array.from({ length: Math.max(1, maxConcurrentCalls) + 1 }, (_, value) => value);
    const tickEvery = Math.max(1, Math.ceil(chartData.length / (isHourly ? 12 : 16)));
    const labelByBucket = new Map(chartData.map(datum => [datum.bucket, datum.label]));
    const tickValues = chartData.filter((_, index) => index % tickEvery === 0).map(datum => datum.bucket);

    // Complex mode order counts are every order on a busy or free line; only a share of them is recaptured, so show that share
    // next to the count (busy: never-order %, no answer: unanswered % × never-order %), matching how the revenue is weighted.
    const unansweredRate = assumptions?.missedWhenFreeRate ?? DEFAULT_RECAPTURED_REVENUE_ASSUMPTIONS.missedWhenFreeRate;
    const lostRate = assumptions?.lostCustomerRate ?? DEFAULT_RECAPTURED_REVENUE_ASSUMPTIONS.lostCustomerRate;
    const busyOrders = recapturedRevenue?.busyLineOrders || 0;
    const freeOrders = recapturedRevenue?.freeLineOrders || 0;
    const busyRecapturedOrders = busyOrders * lostRate;
    const freeRecapturedOrders = freeOrders * unansweredRate * lostRate;
    const formatOrders = (recaptured: number, total: number, kind: string) =>
        `≈${Math.round(recaptured).toLocaleString()} recaptured of ${Math.round(total).toLocaleString()} ${kind}orders`;

    const kpis: Array<{ label: string; value: string; footnote?: string; explanation?: string; isLoading: boolean }> = isComplexMode ? [
        { label: 'Total Recaptured Revenue', value: formatCurrency(recapturedRevenue?.recapturedRevenue), footnote: formatOrders(busyRecapturedOrders + freeRecapturedOrders, recapturedRevenue?.totalOrders || 0, ''), isLoading },
        { label: 'Recaptured Due to Busy Lines', value: formatCurrency(recapturedRevenue?.busyLineRecapturedRevenue), footnote: formatOrders(busyRecapturedOrders, busyOrders, 'busy-line '), isLoading },
        { label: 'Recaptured Due to No Answer', value: formatCurrency(recapturedRevenue?.freeLineRecapturedRevenue), footnote: formatOrders(freeRecapturedOrders, freeOrders, 'free-line '), isLoading }
    ] : [
        { label: 'Est. Recaptured Revenue', value: formatCurrency(estimate.recapturedRevenue), explanation: explainRecapturedRevenueEstimate(missedOrderRate), isLoading: estimateInputs.isLoading }
    ];
    const isChartLoading = isLoading || (!isComplexMode && estimateInputs.isLoading);
    const axisBottom = { tickSize: 0, tickPadding: 10, tickValues, tickRotation: isHourly ? -35 : 0, format: (value: string | number) => labelByBucket.get(String(value)) || String(value) };
    const chartLoading = <ChartLoading><LoadingBar style={{ width: '74%' }} /><LoadingBar style={{ width: '88%' }} /><LoadingBar style={{ width: '62%' }} /></ChartLoading>;

    const complexAssumptionsText = assumptions
        ? `Assumes ${assumptions.staffCount} ${assumptions.staffCount === 1 ? 'staffer' : 'staff'} answering the phones per location, callers would have held for ${assumptions.holdLimitSeconds}s, and ${formatPercent(assumptions.missedWhenFreeRate)} of normal calls are unanswered (industry standard). All of the above assumes ${formatPercent(1 - assumptions.lostCustomerRate)} of those callers still would have ordered by calling back or going online.${isAllLanguagesOnly ? ' Includes all languages.' : ''}`
        : 'Not available for this selection';

    return (
        <Page>
            <PageHeader>
                <PageTitle>{estimateTitle}</PageTitle>
                <HeaderActions>
                    <BackButton onClick={onBack} type="button"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m12 5-7 7 7 7M5 12h14" /></svg> Back to Dashboard</BackButton>
                    {onComplexModeChange && <GearButton type="button" $active={!!isComplexMode} aria-pressed={!!isComplexMode}
                        aria-label={isComplexMode ? 'Switch to simple mode' : 'Switch to complex mode'} title={isComplexMode ? 'Switch to simple mode' : 'Switch to complex mode'}
                        onClick={() => onComplexModeChange(!isComplexMode)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></svg>
                    </GearButton>}
                </HeaderActions>
            </PageHeader>

            <ContentGrid>
                {isComplexMode ? <KpiGrid $columns={kpis.length}>
                    {kpis.map(kpi => (
                        <KpiCard key={kpi.label}>
                            <KpiLabel small>{kpi.label}</KpiLabel>
                            {kpi.isLoading ? <LoadingBar style={{ height: 34, width: '62%' }} /> : <>
                                <KpiValue $characters={kpi.value.length}>{kpi.value}</KpiValue>
                                <KpiFootnote>{kpi.footnote}</KpiFootnote>
                            </>}
                        </KpiCard>
                    ))}
                </KpiGrid> : kpis.map(kpi => (
                    <HeroCard key={kpi.label}>
                        <HeroAmount>
                            <HeroLabel>{kpi.label}{dateRangeLabel && <HeroDateRange>{dateRangeLabel}</HeroDateRange>}</HeroLabel>
                            {kpi.isLoading ? <LoadingBar style={{ height: 44, width: 200 }} /> : <HeroValue>{kpi.value}</HeroValue>}
                        </HeroAmount>
                        <HeroExplanation>{kpi.explanation}</HeroExplanation>
                        {onMissedOrderRateChange && <HeroSlider>
                            <SliderField>
                                <SliderLabel>
                                    <span>What % of calls were you not picking up before Voicify?</span>
                                </SliderLabel>
                                <SliderRow>
                                    <SliderInput type="range" min={0} max={100} step={1} value={missedOrderPercent} style={sliderFill(missedOrderPercent, 0, 100)}
                                        aria-valuetext={`${missedOrderPercent}%`}
                                        onChange={event => onMissedOrderRateChange(Number(event.target.value) / 100)} />
                                    <LargeSliderValue>{missedOrderPercent}%</LargeSliderValue>
                                </SliderRow>
                                <SliderHint>
                                    Calls that rang out, hit a busy line or hung up on hold.{onSaveMissedOrderRateDefault ? '' : ' Changes update this page and are not saved.'}
                                    <MetricInfoTooltip metricKey="RECAPTURED_REVENUE" iconSize={12} customDefinition={RECAPTURED_REVENUE_DEFINITION} />
                                </SliderHint>
                            </SliderField>
                            {(!isAtDefault || saveDefaultError) && <SliderActions>
                                {!isAtDefault && <ResetLink type="button" disabled={isSavingDefault} onClick={() => onMissedOrderRateChange(defaultMissedOrderRate ?? missedOrderRate)}>Reset to {defaultMissedOrderPercent}%</ResetLink>}
                                {!isAtDefault && onSaveMissedOrderRateDefault && <SaveDefaultButton type="button" disabled={isSavingDefault} onClick={saveDefault}>
                                    {isSavingDefault ? 'Saving…' : `Update default to ${missedOrderPercent}%`}
                                </SaveDefaultButton>}
                                {saveDefaultError && <SaveDefaultError role="alert">{saveDefaultError}</SaveDefaultError>}
                            </SliderActions>}
                        </HeroSlider>}
                    </HeroCard>
                ))}

                {isComplexMode ? <>
                    {!isLoading && <Assumptions>
                        {complexAssumptionsText}
                        <MetricInfoTooltip metricKey="RECAPTURED_REVENUE" iconSize={12} customDefinition={COMPLEX_RECAPTURED_REVENUE_DEFINITION} />
                    </Assumptions>}
                    {onAssumptionsChange && <Card>
                        <SliderCardHeader>
                            <div>
                                <CardTitle>Assumptions</CardTitle>
                                <CardSubtitle>{isRecalculating ? 'Recalculating…' : 'Complex mode. Changes update this page and are not saved.'}</CardSubtitle>
                            </div>
                            <ResetLink type="button" disabled={!editedAssumptions} onClick={() => onAssumptionsChange(null)}>Reset to defaults</ResetLink>
                        </SliderCardHeader>
                        <SliderGrid>
                            {ASSUMPTION_SLIDERS.map(slider => {
                                const value = getSliderValue(slider.key, slider.isRate);
                                return (
                                    <SliderField key={slider.key}>
                                        <SliderLabel><span>{slider.label}</span><SliderValue>{slider.format(value)}</SliderValue></SliderLabel>
                                        <SliderInput type="range" min={slider.min} max={slider.max} step={slider.step} value={value} style={sliderFill(value, slider.min, slider.max)}
                                            aria-valuetext={slider.format(value)}
                                            onChange={event => setSliderValue(slider.key, Number(event.target.value), slider.isRate)} />
                                        <SliderHint>{slider.hint}</SliderHint>
                                    </SliderField>
                                );
                            })}
                        </SliderGrid>
                    </Card>}
                </> : !onMissedOrderRateChange && <Assumptions>
                    {`Assumes you weren't picking up ${missedOrderPercent}% of calls before Voicify.`}
                    <MetricInfoTooltip metricKey="RECAPTURED_REVENUE" iconSize={12} customDefinition={RECAPTURED_REVENUE_DEFINITION} />
                </Assumptions>}

                <Card>
                    <TileHeader style={{ justifyContent: 'flex-start' }}>
                        <div>
                            <CardTitle>{estimateTitle}</CardTitle>
                            {!isComplexMode && isAllLanguagesOnly && <CardSubtitle>Spread over time by when orders came in across all languages.</CardSubtitle>}
                        </div>
                    </TileHeader>
                    <ChartArea>
                        {isChartLoading ? chartLoading : hasChartData ? <ResponsiveBar
                            data={chartData}
                            keys={[RECAPTURED_KEY]}
                            indexBy="bucket"
                            margin={{ top: 16, right: 16, bottom: 48, left: 72 }}
                            padding={barChartDefaults.padding}
                            colors={[chart_planned_solid]}
                            borderRadius={barChartDefaults.borderRadius}
                            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 5, format: value => formatCurrency(Number(value)) }}
                            axisBottom={axisBottom}
                            enableGridX={false}
                            enableGridY={true}
                            enableLabel={false}
                            theme={nivoChartTheme}
                            animate={barChartDefaults.animate}
                            motionConfig={barChartDefaults.motionConfig}
                            tooltip={({ data, color }) => <ChartTooltip title={String(data.label)} value={formatCurrency(Number(data.total))}
                                valueLabel="recaptured" showApps={false} color={color} />}
                            role="application"
                            ariaLabel="Recaptured revenue"
                        /> : <EmptyState>No orders</EmptyState>}
                    </ChartArea>
                </Card>

                <Card>
                    <TileHeader style={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div>
                            <CardTitle>Max Concurrent Calls per Location</CardTitle>
                            <CardSubtitle>Calls at the same time at a single store, not added up across stores. Each bar shows the busiest selected location.</CardSubtitle>
                        </div>
                        <MetricInfoTooltip metricKey="RECAPTURED_REVENUE_MAX_CONCURRENT_CALLS" iconSize={12} customDefinition={{
                            name: 'Max Concurrent Calls per Location',
                            description: 'Most calls the assistant handled at once at one location. Calls at different locations are never added together. A transferred call counts only until the transfer. With several locations selected, each bar is the busiest location.',
                            calculation: ''
                        }} />
                    </TileHeader>
                    <ChartArea>
                        {isLoading ? chartLoading : hasConcurrencyData ? <ResponsiveBar
                            data={chartData}
                            keys={['maxConcurrentCalls']}
                            indexBy="bucket"
                            margin={{ top: 16, right: 16, bottom: 48, left: 72 }}
                            padding={barChartDefaults.padding}
                            colors={[chart_planned_solid]}
                            borderRadius={barChartDefaults.borderRadius}
                            valueScale={{ type: 'linear', min: 0, max: concurrencyTicks[concurrencyTicks.length - 1] }}
                            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: concurrencyTicks.length > 8 ? 5 : concurrencyTicks }}
                            axisBottom={axisBottom}
                            enableGridX={false}
                            enableGridY={true}
                            enableLabel={false}
                            theme={nivoChartTheme}
                            animate={barChartDefaults.animate}
                            motionConfig={barChartDefaults.motionConfig}
                            tooltip={({ data, color }) => <ChartTooltip title={String(data.label)} value={Number(data.maxConcurrentCalls).toLocaleString()}
                                valueLabel="calls at once (busiest location)" showApps={false} color={color} />}
                            role="application"
                            ariaLabel="Max concurrent calls"
                        /> : <EmptyState>No calls</EmptyState>}
                    </ChartArea>
                </Card>
            </ContentGrid>
        </Page>
    );
};

export default RecapturedRevenueDashboard;

const Page = styled.div`
    background: #f4f6f9;
    color: #1e293b;
    min-height: 100%;
    padding: 0 0 24px;
`;
const PageHeader = styled.header`align-items: center; display: flex; flex-wrap: wrap; gap: 16px; justify-content: space-between; margin-bottom: 20px;`;
const PageTitle = styled.h1`border-left: 4px solid ${voicify_spark_darker}; color: ${color_shades_darkest}; font-size: 24px; font-weight: 600; letter-spacing: -.6px; line-height: 1.2; margin: 0; padding-left: 12px; ${breakpoint_macbook_small} { font-size: 16px; } ${breakpoint_macbook} { font-size: 20px; } ${breakpoint_large} { font-size: 13px; }`;
const BackButton = styled(ViewDetailsButton)`font: inherit; font-size: 13px; gap: 7px; min-height: 36px; padding: 8px 14px; &:focus-visible { outline: 2px solid ${voicify_spark_darker}; outline-offset: 3px; } svg { flex-shrink: 0; }`;
const HeaderActions = styled.div`align-items: center; display: flex; flex-wrap: wrap; gap: 8px;`;
const GearButton = styled(BackButton)<{ $active: boolean }>`background: ${props => props.$active ? voicify_spark_lightest : '#fff'}; border: 1px solid ${props => props.$active ? voicify_spark_darker : color_shades_light}; color: ${voicify_spark_darkest}; justify-content: center; padding: 8px 10px; &:hover:not(:disabled) { background: ${voicify_spark_lightest}; border-color: ${voicify_spark_darker}; }`;
const CardSubtitle = styled.div`color: ${color_text_dashboard_secondary}; font-size: 12px; margin-top: 5px;`;
const SliderGrid = styled.div`display: grid; gap: 20px 32px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); margin-top: 14px;`;
const SliderField = styled.label`display: grid; gap: 6px;`;
const SliderLabel = styled.span`align-items: baseline; color: ${color_shades_darkest}; display: flex; font-size: 13px; font-weight: 600; gap: 8px; justify-content: space-between;`;
const SliderValue = styled.span`color: ${voicify_spark_darkest}; font-variant-numeric: tabular-nums;`;
const SliderInput = styled.input`
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
    height: 22px;
    margin: 0;
    width: 100%;
    &::-webkit-slider-runnable-track { background: linear-gradient(to right, ${voicify_spark_dark} var(--fill, 0%), ${voicify_spark_lighter} var(--fill, 0%)); border-radius: 999px; height: 6px; }
    &::-webkit-slider-thumb { -webkit-appearance: none; background: #fff; border: 2px solid ${voicify_spark_dark}; border-radius: 50%; box-shadow: 0 1px 3px rgba(15, 23, 42, .18); height: 18px; margin-top: -6px; width: 18px; }
    &::-moz-range-track { background: ${voicify_spark_lighter}; border-radius: 999px; height: 6px; }
    &::-moz-range-progress { background: ${voicify_spark_dark}; border-radius: 999px; height: 6px; }
    &::-moz-range-thumb { background: #fff; border: 2px solid ${voicify_spark_dark}; border-radius: 50%; box-shadow: 0 1px 3px rgba(15, 23, 42, .18); height: 14px; width: 14px; }
    &:focus-visible { outline: 2px solid ${voicify_spark_darker}; outline-offset: 4px; }
`;
const SliderHint = styled.span`align-items: center; color: ${color_text_dashboard_secondary}; display: flex; flex-wrap: wrap; font-size: 11px; gap: 6px;`;
const SliderCardHeader = styled.div`align-items: flex-start; display: flex; flex-wrap: wrap; gap: 8px 16px; justify-content: space-between;`;
const SliderRow = styled.span`align-items: center; display: flex; gap: 16px;`;
const LargeSliderValue = styled(SliderValue)`flex-shrink: 0; font-size: 28px; font-weight: 600; min-width: 3.2em; text-align: right;`;
const SliderActions = styled.div`align-items: center; display: flex; flex-wrap: wrap; gap: 8px 16px; justify-content: flex-end; margin-top: 12px;`;
const SaveDefaultButton = styled(BackButton)`&:disabled { cursor: default; opacity: .6; }`;
const SaveDefaultError = styled.span`color: #b91c1c; font-size: 12px; width: 100%; text-align: right;`;
const ResetLink = styled.button`background: transparent; border: 0; color: ${voicify_spark_darkest}; cursor: pointer; font: inherit; font-size: 12px; font-weight: 600; padding: 0; &:disabled { color: #94a3b8; cursor: default; }`;
const ContentGrid = styled.div`display: grid; gap: 16px;`;
const KpiGrid = styled.div<{ $columns: number }>`display: grid; gap: 12px; grid-template-columns: repeat(${props => props.$columns}, minmax(0, 1fr)); @media (max-width: 1000px) { grid-template-columns: repeat(${props => Math.min(2, props.$columns)}, minmax(0, 1fr)); } @media (max-width: 700px) { grid-template-columns: 1fr; }`;
const KpiCard = styled(BaseTile)`container-type: inline-size; align-items: center; justify-content: flex-start; min-width: 0; padding: 16px; text-align: center;`;
const KpiLabel = styled(TileTitle)`flex-shrink: 0; line-height: 1.3; margin: 0; width: 100%;`;
const KpiValue = styled(BigNumber)<{ $characters?: number }>`
    font-size: min(clamp(24px, 4dvh, 38px), ${props => 100 / (Math.max(4, props.$characters || 4) * .7)}cqi);
    margin-top: 6px;
`;
const HeroCard = styled(BaseTile)`
    align-items: center;
    border-left: 4px solid ${voicify_spark_darker};
    display: grid;
    gap: 16px 36px;
    grid-template-columns: auto minmax(0, 1fr) minmax(280px, 380px);
    padding: 24px 32px;
    @media (max-width: 1200px) { grid-template-columns: auto minmax(0, 1fr); }
    @media (max-width: 700px) { grid-template-columns: 1fr; padding: 20px; text-align: center; }
`;
const HeroSlider = styled.div`
    border-left: 1px solid ${color_shades_light};
    padding-left: 36px;
    text-align: left;
    @media (max-width: 1200px) { border-left: 0; border-top: 1px solid ${color_shades_light}; grid-column: 1 / -1; padding: 20px 0 0; }
`;
const HeroAmount = styled.div`display: grid; gap: 10px; justify-items: start; @media (max-width: 700px) { justify-items: center; }`;
const HeroLabel = styled.span`align-items: center; color: ${color_text_dashboard_secondary}; display: flex; flex-wrap: wrap; font-size: 12px; font-weight: 600; gap: 6px 10px; letter-spacing: .08em; text-transform: uppercase; @media (max-width: 700px) { justify-content: center; }`;
const HeroDateRange = styled.span`background: ${voicify_spark_lightest}; border-radius: 999px; color: ${voicify_spark_darkest}; font-weight: 600; letter-spacing: 0; padding: 3px 10px; text-transform: none;`;
const HeroValue = styled(BigNumber)`color: ${voicify_spark_darkest}; font-size: clamp(36px, 4.5vw, 52px); font-variant-numeric: tabular-nums; letter-spacing: -1px;`;
const HeroExplanation = styled.p`
    border-left: 1px solid ${color_shades_light};
    color: ${color_shades_darkest};
    font-size: 20px;
    font-weight: 500;
    line-height: 1.4;
    margin: 0;
    max-width: 540px;
    padding-left: 36px;
    @media (max-width: 700px) { border-left: 0; border-top: 1px solid ${color_shades_light}; font-size: 17px; justify-self: center; padding: 16px 0 0; }
`;
const KpiFootnote = styled(NumberSubtext)`max-width: 100%; margin-top: 6px; line-height: 1.3;`;
const Card = styled(BaseTile.withComponent('section'))`min-width: 0;`;
const CardTitle = styled(TileTitle.withComponent('h2'))`text-align: left; margin: 0;`;
const Assumptions = styled.div`align-items: center; color: ${color_text_dashboard_secondary}; display: flex; font-size: 12px; gap: 6px; justify-content: center;`;
const ChartArea = styled.div`height: 360px; width: 100%; @media (max-width: 650px) { height: 320px; }`;
const EmptyState = styled.div`align-items: center; color: #94a3b8; display: flex; font-size: 13px; height: 100%; justify-content: center; padding: 14px; text-align: center;`;
const LoadingBar = styled.div`animation: recaptured-loading 1.35s ease-in-out infinite; background: linear-gradient(90deg, #e8edf3 25%, #f5f7fa 45%, #e8edf3 65%); background-size: 300% 100%; border-radius: 6px; height: 14px; @keyframes recaptured-loading { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }`;
const ChartLoading = styled.div`align-items: flex-start; display: flex; flex-direction: column; gap: 24px; height: 100%; justify-content: center; padding: 28px 14px;`;
